import React, { useEffect, useState } from 'react';
import { Box, Backdrop, Button, TextField, Typography, InputAdornment, IconButton, Divider, Link, Paper, Container, Grow, DialogContent, Dialog, DialogTitle, DialogActions } from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff, CreditCard, CheckCircle } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import { useParams } from 'react-router';
import styles from "./index.module.scss";

import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Import the new API system
import API from '../../api';

import { InputField } from 'JsUtils/FormikControls';
import CustomModal from 'Common/CustomModal';
import { useNavigation } from 'Hooks/navigate';
import CustomButton from 'Common/CustomButton';
import useKeycloakAdapter from 'Hooks/useKeycloakAdapter';

const SignInForm = () => {
    const { goToDashboard, goToAdvisorRegistration, goToClientRegistration, goToSubscription } = useNavigation()
    const params = useParams();
    const [showPassword, setShowPassword] = useState(false);
    const [backdrop, setBackdrop] = useState(false);
    const [userType, setUserType] = useState(""); // Default to advisor
    const [mfaRequired, setMfaRequired] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    // Forgot Password Modal State - NEW
    const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
    const [forgotPasswordError, setForgotPasswordError] = useState('');

    // KEYCLOAK JS ADAPTER STATE - REPLACES IFRAME
    const [showKeycloakSetup, setShowKeycloakSetup] = useState(false);
    const [setupCheckLoading, setSetupCheckLoading] = useState(false);
    const [setupPopup, setSetupPopup] = useState(null);
    const keycloakAdapter = useKeycloakAdapter();
    const disablePricing = true; // Set to true to disable pricing page access

    useEffect(() => {
        setUserType(params.type)
    }, [params])

    /**
     * Open Keycloak account management popup for setup
     * User completes setup in popup, then clicks "Setup Complete" button
     */
    const openKeycloakSetup = async () => {
        try {
            const realm = userType === 'advisor' ? 'TASAdvisor' : 'TASClient';
            const clientIdForKeycloak = userType === 'client' ? `tasclientauth` : "tasadvisorauth";

            // Skip initialization - directly open account management
            const popup = keycloakAdapter.openAccountManagement(realm, clientIdForKeycloak);
            
            if (popup) {
                setSetupPopup(popup);
                toast.info('📱 Account setup window opened. Please complete your setup and click "Setup Complete" when done.');
            } else {
                toast.error('Failed to open setup window. Please check if popups are blocked.');
            }
        } catch (err) {
            console.error('Failed to open Keycloak setup:', err);
            toast.error('Failed to open setup window. Please try again.');
        }
    };

    // Monitor popup window: when it closes, automatically attempt verification
    useEffect(() => {
        let timer;
        if (setupPopup) {
            timer = setInterval(() => {
                try {
                    if (setupPopup.closed) {
                        clearInterval(timer);
                        setSetupPopup(null);
                        toast.info('Setup window closed. Verifying setup...');
                        // Auto-check setup completion when popup closes
                        checkSetupCompletion(true);
                    }
                } catch (e) {
                    // Accessing popup properties can throw if cross-origin; ignore and continue
                }
            }, 1000);
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [setupPopup]);

    // Cleanup popup if component unmounts
    useEffect(() => {
        return () => {
            try {
                if (setupPopup && !setupPopup.closed) {
                    setupPopup.close();
                }
            } catch (e) {
                // ignore
            }
        };
    }, [setupPopup]);

    const checkSetupCompletion = async (showLoadingState = true) => {
        if (!username || !password) {
            toast.error('Session expired. Please try logging in again.');
            setShowKeycloakSetup(false);
            return;
        }

        if (showLoadingState) {
            setSetupCheckLoading(true);
        }

        try {
            const loginData = {
                username: username,
                password: password,
                userType: userType
            };

            console.log('🔄 Verifying setup completion...');
            const response = await API.auth.login(loginData);

            if (response.status === 200 || response.status === 201) {
                setShowKeycloakSetup(false);
                toast.success('🎉 Perfect! Login successful!');
                handleSuccessfulLogin(response.data);
            } else if (response.status === 203 && response.data.mfaRequired) {
                setShowKeycloakSetup(false);
                setMfaRequired(true);
                toast.success('✅ Great! Now enter your authentication code.');
            }
        } catch (err) {
            console.log('Setup check result:', err?.response?.status);

            // Provide specific feedback based on error
            if (err?.response?.status === 428 ||
                (err?.response?.status === 400 &&
                    err?.response?.data?.error_description?.includes('not fully set up'))) {
                toast.warning('Setup still in progress. Please complete all steps above and try again.');
            } else if (err?.response?.status === 401) {
                toast.warning('Please complete the authenticator setup and try again.');
            } else {
                toast.error('Unable to verify setup. Please try logging in again after completing setup.');
            }
        } finally {
            if (showLoadingState) {
                setSetupCheckLoading(false);
            }
        }
    };

    // NEW - Handle Forgot Password Modal
    const handleOpenForgotPasswordModal = () => {
        setForgotPasswordModalOpen(true);
        setForgotPasswordEmail('');
        setForgotPasswordSuccess(false);
        setForgotPasswordError('');
    };

    const handleCloseForgotPasswordModal = () => {
        setForgotPasswordModalOpen(false);
        setForgotPasswordEmail('');
        setForgotPasswordSuccess(false);
        setForgotPasswordError('');
    };

    const handleForgotPasswordSubmit = async () => {
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!forgotPasswordEmail || !emailRegex.test(forgotPasswordEmail)) {
            setForgotPasswordError('Please enter a valid email address');
            return;
        }

        setForgotPasswordLoading(true);
        setForgotPasswordError('');

        try {
            const response = await API.auth.requestPasswordReset({
                email: forgotPasswordEmail,
                userType: userType
            });

            if (response.data.success) {
                setForgotPasswordSuccess(true);
                // Auto-close modal after 2 seconds
                setTimeout(() => {
                    handleCloseForgotPasswordModal();
                }, 2000);
            } else {
                setForgotPasswordError(response.data.message || 'Failed to send reset link');
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            const errorMessage = error.response?.data?.message || 'Failed to send reset link';
            setForgotPasswordError(errorMessage);
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    // FORMIK INITIAL VALUES - Keep as is
    const initialValues = {
        username: '',
        password: '',
    }

    // MFA FORM INITIAL VALUES - Keep as is
    const mfaInitialValues = {
        otpCode: '',
    }

    // YUP VALIDATION SCHEMA - Keep as is
    const validationSchema = Yup.object({
        username: Yup.string().required('Username is required'),
        password: Yup.string().required('Password is required'),
    })

    // YUP VALIDATION SCHEMA FOR MFA - Keep as is
    const mfaValidationSchema = Yup.object({
        otpCode: Yup.string().required('Authentication code is required')
            .min(6, 'Code must be at least 6 characters')
            .max(10, 'Code cannot be more than 10 characters')
    })

    // HANDLE REGULAR LOGIN SUBMIT - REMOVED autoCheckEnabled references
    const HandleSubmit = async (values) => {
        // setBackdrop(true);
        setUsername(values.username);
        setPassword(values.password);

        const loginData = {
            username: values.username,
            password: values.password,
            userType: userType
        };

        try {
            const response = await API.auth.login(loginData);

            if (response.status === 203 && response.data.mfaRequired) {
                setMfaRequired(true);
                // setSessionId(response.data.sessionId || '');
                setBackdrop(false);
                toast.info('Please enter your authentication code');
            } else if (response.status === 200 || response.status === 201) {
                handleSuccessfulLogin(response.data);
            } else {
                if (response.data.message) {
                    toast.error(response.data.message);
                } else {
                    toast.error('Invalid Credentials');
                }
                setBackdrop(false);
            }
        } catch (err) {
            setBackdrop(false);

            // SHOW POPUP-BASED KEYCLOAK SETUP FOR SETUP REQUIRED - USES JS ADAPTER
            if (err?.response?.status === 428 && err?.response?.data?.setupRequired) {
                setShowKeycloakSetup(true);
                toast.info('Please complete your account setup in the popup window');
            }
            else if (err?.response?.status === 400 &&
                err?.response?.data?.error === 'invalid_grant' &&
                err?.response?.data?.error_description?.includes('Account is not fully set up')) {

                setShowKeycloakSetup(true);
                toast.info('Please complete your account setup in the popup window');
            } else {
                console.log('Login error:', err?.response?.data?.message);
                // toast.error(err?.response?.data?.message || 'Invalid Credentials');
            }
        } finally {
            setBackdrop(false);
        }
    }

    // HANDLE MFA SUBMIT - Keep as is
    const HandleMfaSubmit = async (values) => {
        // setBackdrop(true);

        // Create the complete MFA data object
        const mfaData = {
            username: username,
            password: password,
            userType: userType,
            otpCode: values.otpCode
        };

        try {
            // Use new API system for MFA too
            const response = await API.auth.login(mfaData);

            if (response.status === 200 || response.status === 201) {
                handleSuccessfulLogin(response.data);
            } else {
                if (response.data.message) {
                    toast.error(response.data.message);
                } else {
                    toast.error('Invalid Authentication Code');
                }
                setBackdrop(false);
            }
        } catch (err) {
            setBackdrop(false);
            toast.error(err?.response?.data?.message || 'Invalid Authentication Code');
        }
    }

    // HANDLE SUCCESSFUL LOGIN - Keep exactly as is
    const handleSuccessfulLogin = (responseData) => {
        localStorage.clear();

        const userRole = responseData.userMapping?.entityType?.toLowerCase() || "";
        const userId = responseData.userMapping?.entityId || "";

        const secondAppUrl = process.env.REACT_APP_TAS_FE_LOCAL

        const redirectUrl = `${secondAppUrl}?token=${responseData.access_token}&refresh_token=${responseData.refresh_token}&user_role=${userRole}&user_id=${userId}&keycloak_user_id=${responseData.userMapping?.keycloakUserId || ""}&user_type=${responseData.userType}`;
        // tasAdmin login
        // const redirectUrl = `${secondAppUrl}?token=${responseData.access_token}&refresh_token=${responseData.refresh_token}&user_role=tasadmin&user_id=${responseData.userId}&keycloak_user_id=${responseData.userId}&user_type=tasadmin`;
        
        window.location.href = redirectUrl;
    }

    const renderKeycloakSetupModal = () => {
        return (
            <>
                <CustomModal
                    open={showKeycloakSetup}
                    handleClose={() => setShowKeycloakSetup(false)}
                    title="Complete Account Setup"
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogContent sx={{ p: 0, height: '100%' }}>
                        {/* Instructions */}
                        <Box sx={{
                            p: 4,
                            backgroundColor: '#e8f4f8',
                            borderBottom: '2px solid #0A48B3'
                        }}>
                            <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                                📱 Setup Your Authenticator
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 2 }}>
                                Follow these steps:
                            </Typography>
                            <Typography variant="body1" component="div" sx={{
                                fontSize: '1rem',
                                lineHeight: 1.8,
                                '& br': { content: '""', display: 'block', margin: '8px 0' }
                            }}>
                                <strong>1.</strong> Click "Open Setup Window" to start<br />
                                <strong>2.</strong> Open your authenticator app (Google Authenticator, Authy, etc.)<br />
                                <strong>3.</strong> Scan the QR code in the setup window<br />
                                <strong>4.</strong> Enter the 6-digit code from your app<br />
                                <strong>5.</strong> Click "Save" or "Submit" in the setup form<br />
                                <strong>6.</strong> Close the setup window and click "Setup Complete" below
                            </Typography>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{
                            p: 3,
                            backgroundColor: '#f0f8ff',
                            borderTop: '2px solid #0A48B3',
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'center',
                            flexDirection: 'column',
                            alignItems: 'stretch'
                        }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={openKeycloakSetup}
                                disabled={keycloakAdapter.isLoading}
                                startIcon={keycloakAdapter.isLoading ? <CircularProgress size={16} color="inherit" /> : <VerifiedUserIcon />}
                                sx={{
                                    fontSize: '1rem',
                                    py: 1.5
                                }}
                            >
                                {keycloakAdapter.isLoading ? 'Opening...' : '🔧 Open Setup Window'}
                            </Button>

                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                onClick={() => checkSetupCompletion(true)}
                                disabled={setupCheckLoading}
                                startIcon={setupCheckLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                                sx={{
                                    fontSize: '1rem',
                                    py: 1.5
                                }}
                            >
                                {setupCheckLoading ? 'Verifying Setup...' : '✅ Setup Complete - Continue'}
                            </Button>
                        </Box>

                        {/* Help Text */}
                        <Box sx={{
                            p: 2,
                            backgroundColor: '#fff3cd',
                            borderTop: '1px solid #ffeaa7',
                            textAlign: 'center'
                        }}>
                            <Typography variant="caption" color="text.secondary">
                                💡 <strong>Tip:</strong> The setup window opens in a separate popup. Please allow popups if prompted. After completing your authenticator setup in the popup, close it and click "Setup Complete" to continue.
                            </Typography>
                        </Box>
                    </DialogContent>
                </CustomModal>
            </>
        );
    };

    // NEW - Forgot Password Modal
    const renderForgotPasswordModal = () => {
        return (
            <Dialog
                open={forgotPasswordModalOpen}
                onClose={handleCloseForgotPasswordModal}
                maxWidth="sm"
                fullWidth
            >
                {!forgotPasswordSuccess ? (
                    <>
                        <DialogTitle>Reset Password</DialogTitle>
                        <DialogContent>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Enter your email address and we'll send you a link to reset your password.
                                </Typography>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={forgotPasswordEmail}
                                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                    placeholder="Enter your registered email"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email sx={{ color: 'action.active' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    error={!!forgotPasswordError}
                                    helperText={forgotPasswordError}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseForgotPasswordModal} disabled={forgotPasswordLoading}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleForgotPasswordSubmit}
                                variant="contained"
                                disabled={forgotPasswordLoading}
                                sx={{ backgroundColor: '#0A48B3' }}
                            >
                                {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogContent>
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <CheckCircle sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Success!
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    Password reset link sent successfully
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Please check your email inbox
                                </Typography>
                            </Box>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        );
    };

    // MFA FORM ELEMENT - Keep exactly as is
    const renderMfaForm = () => {
        return (
            <Formik initialValues={mfaInitialValues} validationSchema={mfaValidationSchema} onSubmit={HandleMfaSubmit}>
                {() => (
                    <Form>
                        <Grow in timeout={1000}>
                            <Box>
                                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                                        Please enter the authentication code from your authenticator app
                                    </Typography>
                                </Box>

                                {/* OTP CODE INPUT */}
                                <div className={styles.inputContainer}>
                                    <InputField
                                        name="otpCode"
                                        type="text"
                                        placeholder="Authentication Code *"
                                        className={styles.inputField}
                                        maxLength={10}
                                        adornment={<VerifiedUserIcon />}
                                    />
                                </div>

                                <div>
                                    <Button
                                        sx={{ background: '#0A48B3', width: '100%', padding: '10px', mt: 2 }}
                                        type='submit'
                                        variant="contained"
                                    >
                                        Verify Code
                                    </Button>
                                </div>

                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: '#0A48B3', cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={() => setMfaRequired(false)}
                                    >
                                        Back to Login
                                    </Typography>
                                </Box>
                            </Box>
                        </Grow>
                    </Form>
                )}
            </Formik>
        );
    }

    const renderNewLoginForm = () => {
        return (
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={HandleSubmit}>
                {(formikProps) => (
                    <Form>
                        <Grow in timeout={1000}>
                            <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400, }}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    margin="normal"
                                    name="username"
                                    type="email"
                                    placeholder="Email *"
                                    maxLength={50}
                                    value={formikProps.values.username}
                                    onChange={(e) => formikProps.setFieldValue('username', e.target.value)}
                                    onBlur={formikProps.handleBlur}
                                    error={formikProps.touched.username && Boolean(formikProps.errors.username)}
                                    helperText={formikProps.touched.username && formikProps.errors.username}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Password"
                                    margin="normal"
                                    name="password"
                                    placeholder="Password *"
                                    type={showPassword ? 'text' : 'password'}
                                    maxLength={50}
                                    value={formikProps.values.password}
                                    onChange={(e) => formikProps.setFieldValue('password', e.target.value)}
                                    onBlur={formikProps.handleBlur}
                                    error={formikProps.touched.password && Boolean(formikProps.errors.password)}
                                    helperText={formikProps.touched.password && formikProps.errors.password}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(prev => !prev)}>
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Box display="flex" justifyContent="flex-end" mb={2}>
                                    <Link
                                        onClick={handleOpenForgotPasswordModal}
                                        underline="hover"
                                        variant="body2"
                                        sx={{ cursor: "pointer" }}
                                    >
                                        Forgot Your Password?
                                    </Link>
                                </Box>

                                <Box display="flex" gap={2} mb={2}>
                                    <CustomButton size='custom' padding="10px 20px" fontSize="14px" fullWidth variant="outlined" onClick={() => goToDashboard()}>
                                        BACK
                                    </CustomButton>
                                    <CustomButton size='custom' padding="10px 20px" fontSize="14px" fullWidth variant="contained" color="primary" type='submit'>
                                        LOG IN
                                    </CustomButton>
                                </Box>

                                <Divider>OR</Divider>

                                <CustomButton
                                    fullWidth
                                    variant="gradient"
                                    onClick={() => goToSubscription(params.type)}
                                    startIcon={<CreditCard />}
                                    disabled={disablePricing}
                                    sx={{ mt: 2, mb: 1, color: disablePricing ? '#040404' : '#f2f2f2', backgroundColor: disablePricing ? '#b0b0b0' : '#00c853', '&:hover': { backgroundColor: disablePricing ? '#b0b0b0' : '#00b248' } }}
                                >
                                    Subscribe to a Package
                                </CustomButton>

                                <Box textAlign="center" fontSize="0.875rem" color="gray">
                                    🔒 Secure payment powered by Stripe<br />
                                </Box>

                                <Box mt={2} textAlign="center">
                                    <Link
                                        underline="hover"
                                        fontSize="0.9rem"
                                        onClick={() => userType === "advisor" ? goToAdvisorRegistration() : goToClientRegistration()}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        Create new account? Sign-up
                                    </Link>
                                </Box>
                            </Paper>
                        </Grow>
                    </Form>
                )}
            </Formik>
        );
    }

    return (
        <Container maxWidth='xs'>
            {mfaRequired ? renderMfaForm() : renderNewLoginForm()}

            {/* BUTTON-BASED KEYCLOAK SETUP MODAL */}
            {showKeycloakSetup && renderKeycloakSetupModal()}

            {/* NEW - FORGOT PASSWORD MODAL */}
            {forgotPasswordModalOpen && renderForgotPasswordModal()}

            {/* BACKDROP LOADER */}
            <Backdrop open={backdrop}>
                <CircularProgress style={{ color: 'white' }} />
            </Backdrop>
        </Container>
    );
}

export default SignInForm